'use client';

import React, { useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { H3, P } from '@osmosis/tailwind-ui/components/Text';
import { borderNeutral300 } from '@osmosis/tailwind-ui/styles/border';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  ANALYTICS_LABELS,
  EXAMPLE_PROMPTS,
  PromptId,
} from '../../lib/util/examplePrompts';
import { OAIMode } from '../../lib/util/oai';
import { track } from '../../lib/util/tracking';
import { usePagesContext } from '@osmosis/nextjs-components/context/PagesProvider';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { useAIPanelContext } from '../../context/AIPanelContext';
import { TrustStrip } from './TrustStrip';

type Props = {
  topic?: string;
  onPromptSelected: (prompt: string, mode: OAIMode) => void;
  collapsed: boolean;
  togglePromptsSection: () => void;
  showExamplePrompts: boolean;
  /**
   * When true the example prompts are hidden entirely (only the TrustStrip
   * remains). Set once the user submits a prompt; independent of the
   * collapse/expand toggle.
   */
  promptSubmitted?: boolean;
};

const TOGGLE_LABELS = defineMessages({
  show: {
    id: 'ai-panel.show-prompts',
    defaultMessage: 'Show example prompts for {topic}',
  },
  hide: {
    id: 'ai-panel.hide-prompts',
    defaultMessage: 'Hide example prompts for {topic}',
  },
});

/**
 * ExamplePrompts component.
 */
export const ExamplePrompts = ({
  topic = '',
  onPromptSelected,
  collapsed,
  togglePromptsSection,
  showExamplePrompts,
  promptSubmitted = false,
}: Props) => {
  const { formatMessage } = useIntl();

  const { isOpen } = useAIPanelContext();

  const { user, isUserLoaded } = useUserContext();
  const visiblePrompts = user?.isFaculty
    ? EXAMPLE_PROMPTS.filter((p) => p.id !== 'tutor')
    : EXAMPLE_PROMPTS;

  const { pathname } = usePagesContext();

  const onTogglePrompts = () => {
    const eventName = collapsed
      ? 'osmosis-ai-example-prompt-expanded'
      : 'osmosis-ai-example-prompt-collapsed';
    track(eventName, { page: pathname }, ['keen', 'pendo']);

    togglePromptsSection();
  };

  const shownPageRef = useRef(false);

  useEffect(() => {
    if (shownPageRef.current || !isOpen || !showExamplePrompts) return;
    shownPageRef.current = true;
    track('osmosis-ai-example-prompt-shown', { page: pathname }, [
      'keen',
      'pendo',
    ]);
  }, [pathname, isOpen, showExamplePrompts]);

  const handleChipClick = (id: PromptId) => {
    track(
      'osmosis-ai-example-prompt-clicked',
      { prompt: ANALYTICS_LABELS[id], page: pathname },
      ['keen', 'pendo'],
    );
    const def = visiblePrompts.find((p) => p.id === id);
    if (!def) return;
    onPromptSelected(formatMessage(def.prompt), def.mode);
  };

  const triggerId = `example-prompts-toggle-${topic}`;

  const panelId = `oai-example-prompts-list`;

  return (
    <Box data-testid="example-prompts">
      <TrustStrip />

      {showExamplePrompts && !promptSubmitted ? (
        <Box className="px-4 pt-1 pb-0">
          {/* eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers */}
          <Flex
            className="w-full items-center justify-between gap-2 px-0 cursor-pointer"
            data-testid="example-prompts-header"
            onClick={onTogglePrompts}
          >
            <H3 className={`text-base font-bold ${text5} text-left`}>
              {formatMessage(
                {
                  id: 'ai-panel.studying',
                  defaultMessage: 'Studying {topic}?',
                },
                { topic: topic.toLowerCase().trim() },
              )}
            </H3>
            <Button
              id={triggerId}
              variant="transparent"
              color="transparent"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                e.preventDefault();
                onTogglePrompts();
              }}
              aria-expanded={!collapsed}
              aria-controls={panelId}
              aria-label={formatMessage(
                collapsed ? TOGGLE_LABELS.show : TOGGLE_LABELS.hide,
                { topic },
              )}
              data-testid="example-prompts-toggle-button"
            >
              <Icon
                icon={faChevronDown}
                size="1x"
                className={cn(
                  text5,
                  'transition-transform duration-200 ease-in-out',
                  collapsed ? '' : 'rotate-180',
                )}
              />
            </Button>
          </Flex>
          <Box
            id={panelId}
            role="region"
            aria-labelledby={triggerId}
            aria-hidden={collapsed}
            data-testid="example-prompts-list"
            className={cn(
              'grid transition-[grid-template-rows] duration-300 ease-in-out',
              collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
              collapsed && 'pointer-events-none',
            )}
          >
            <Flex className="flex flex-col gap-2 overflow-hidden px-0.5 pb-0.5 pt-2">
              {isUserLoaded
                ? visiblePrompts.map((prompt) => (
                    <Button
                      key={prompt.id}
                      variant="transparent"
                      tabIndex={collapsed ? -1 : 0}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg border px-3 py-3 justify-start text-left',
                        borderNeutral300,
                      )}
                      onClick={() => handleChipClick(prompt.id)}
                      aria-label={formatMessage(prompt.label)}
                      data-testid={`example-prompt-${prompt.id}`}
                    >
                      <Icon
                        icon={prompt.icon.icon}
                        size={20}
                        className={cn(prompt.icon.color, 'shrink-0')}
                      />
                      <P className={`!text-sm font-normal ${text5}`}>
                        <FormattedMessage {...prompt.label} />
                      </P>
                    </Button>
                  ))
                : null}
            </Flex>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ExamplePrompts;
